import React, {useCallback, useMemo} from 'react'
import fireEvent from 'react-app-events/lib/fireEvent'
import Button from '@components/Button'
import PromotionTag from '@components/PromotionTag'
import {ProductListInformationFragment} from '@data/__generated__/types.main'
import {EVENTS} from '@helpers/analytics/events'
import {BrowseTaxonomies} from '@helpers/analytics/events/browse'
import {sendEvent} from '@helpers/analytics/sendEvents'
import productRequiredModifiers from '@helpers/misc/productWithRequiredModifiers'
import selectPriceForProduct from '@helpers/misc/selectPriceForProduct'
import useAnalyticLocation from '@hooks/useAnalyticLocation'
import useEventsBaseProperties from '@hooks/useEventsBaseProperties'
import useOpenProductModal from '@hooks/useOpenProductModal'
import FormatNumber from '@i18n/formatNumber/Component'
import Image from '@page-components/Order/Products/ProductsContent/Category/Product/Image'
import useAdd from '@page-components/Order/Products/ProductsContent/SelectProduct/Inner/useAdd'
import classnames from 'classnames'
import Link from 'next/link'
import {useTranslation} from 'next-i18next'
import InformativeStamps from 'src/components/InformativeStamps'

import PRICING_TYPES from './pricingTypes'

import styles from './styles.module.css'

export interface Props {
  product: ProductListInformationFragment
  menuId: string
  hidePrice?: boolean
  openModal?: () => void
  isStore?: boolean
  channels?: any
  href?: string
  categoryId?: string
  categoryName?: string
}

export default function ProductCard(props: Props) {
  const {product, menuId, hidePrice, isStore, channels, href, categoryId, categoryName} = props
  const {name, images, description, availabilityAt, isOutOfStock} = product
  const add = useAdd()
  const requiredModifiers = productRequiredModifiers(product)
  const pricing = selectPriceForProduct(product)
  const {t} = useTranslation('website', {
    keyPrefix: 'order.categoryPage.content.products.productCard'
  })
  const eventBaseProperties = useEventsBaseProperties()
  const actionLocation = useAnalyticLocation('browse')

  const openProductModalWrapper = useOpenProductModal()
  const openProductModal = useCallback(
    () => openProductModalWrapper(product._id, href),
    [openProductModalWrapper, product._id, href]
  )

  const onClick = () => {
    if (hidePrice) {
      fireEvent('hiddenPriceProductOpened', product)
      return
    }

    sendEvent<BrowseTaxonomies['productSelected']>(EVENTS.browse.productSelected, {
      categoryId,
      categoryName,
      menuId,
      productName: product.name,
      actionLocation: actionLocation,
      productId: product._id,
      ...eventBaseProperties
    })

    openProductModal()
  }

  const setLabel = () => {
    if (availabilityAt.available) {
      const soldout = isStore ? 'labelOutOfStock' : 'labelOutOfStockToday'

      return isOutOfStock ? soldout : 'labelAddProduct'
    }
    return availabilityAt.notAvailableMessage
      ? availabilityAt.notAvailableMessage
      : 'labelProductNotAvailable'
  }
  const informativeStampsList = product?.informativeStamps?.slice(0, 3)

  const handleButtonLabel = useMemo(() => {
    return setLabel()
  }, [isOutOfStock, channels])

  return (
    <div className={classnames(styles.container, isOutOfStock ? styles.containerOutOfStock : '')}>
      <Link href={href} prefetch={false} scroll={false}>
        <div className={styles.image} onClick={onClick}>
          {images && images.length > 0 ? (
            <Image product={product} />
          ) : (
            <div className={styles.imagePlaceholder} />
          )}
        </div>
      </Link>
      <div className={styles.content}>
        <div className={styles.flexColumn}>
          <Link href={href} prefetch={false} scroll={false}>
            <div className={styles.text}>
              <div className={styles.textContent} onClick={onClick}>
                <div className={styles.name}>{name}</div>
                <InformativeStamps stamps={informativeStampsList} iconOnly={true} />
                <div className={styles.description}>{description}</div>
              </div>
              <div className={styles.priceContainer}>
                {pricing && !hidePrice ? (
                  <div className={styles.priceSection} onClick={onClick}>
                    <div className={styles.price} onClick={onClick}>
                      {pricing.type === PRICING_TYPES.normal && (
                        <FormatNumber value={pricing.price} />
                      )}
                      {pricing.type === PRICING_TYPES.discount && (
                        <>
                          <FormatNumber value={pricing.price} />
                          <span>
                            <FormatNumber value={pricing.oldPrice} />
                          </span>
                        </>
                      )}
                    </div>
                    {pricing.type === PRICING_TYPES.promotions && (
                      <PromotionTag product={product} />
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </Link>
          <div className={styles.buttonWrapper}>
            <Button
              primary
              onClick={async () => {
                if (hidePrice) {
                  fireEvent('hiddenPriceProductOpened', product)
                  return null
                }
                if (isOutOfStock) return null

                if (requiredModifiers) {
                  onClick()
                  return
                }
                await add(
                  {...props, close: () => {}, menuId},
                  {amount: 1, modifiers: [], comment: ''},
                  product,
                  {missingModifier: false}
                )
              }}
              disabled={isOutOfStock || !availabilityAt.available}>
              {availabilityAt.notAvailableMessage ? handleButtonLabel : t(handleButtonLabel)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
